import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { RiskBadge, RiskIndicator } from "@/components/RiskBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Search, Ship, Loader2, AlertTriangle, FileText, Building2,
  Flag, Shield, CheckCircle, XCircle, Award, AlertOctagon
} from "lucide-react";
import { toast } from "sonner";

const VesselSearchPage = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState("imo");
  const [imoNumber, setImoNumber] = useState("");
  const [vesselName, setVesselName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [savingReport, setSavingReport] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const payload = searchType === "imo"
        ? { imo_number: imoNumber }
        : { vessel_name: vesselName };

      // Start async search
      const startRes = await axios.post(`${API}/vessels/search`, payload);
      const jobId = startRes.data.job_id;

      // Poll for results
      let attempts = 0;
      const maxAttempts = 60; // 60 * 3s = 3 minutes max
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        attempts++;
        const statusRes = await axios.get(`${API}/vessels/search/status/${jobId}`);
        const data = statusRes.data;

        if (data.status === "completed") {
          setResult(data.result);
          toast.success("Screening completado");
          setLoading(false);
          return;
        } else if (data.status === "error") {
          const code = data.error_code;
          if (code === 503) {
            toast.error(data.error || "Equasis no disponible temporalmente.", { duration: 8000 });
          } else if (code === 404) {
            toast.error(data.error || "Buque no encontrado en Equasis.");
          } else {
            toast.error(data.error || "Error en la búsqueda");
          }
          setLoading(false);
          return;
        }
        // Still processing - continue polling
      }
      toast.error("La búsqueda tardó demasiado. Inténtalo de nuevo.");
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Error en la búsqueda");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReport = async () => {
    if (!result?.vessel_screening) {
      toast.error("No screening results to save");
      return;
    }
    setSavingReport(true);
    try {
      const reportData = {
        vessel_data: result.vessel_data,
        vessel_screening: result.vessel_screening,
        ownership_screenings: result.ownership_screenings || [],
        overall_risk: result.overall_risk,
        risk_factors: result.risk_factors || [],
        recommendations: result.recommendations || [],
        data_sources_consulted: result.data_sources_consulted || [],
        source_statuses: result.source_statuses || [],
        executive_summary: result.executive_summary || null,
        investigation_timeframe: "24 months"
      };
      const res = await axios.post(`${API}/reports`, reportData);
      toast.success("Report saved successfully");
      navigate(`/reports/${res.data.id}`);
    } catch (error) {
      toast.error("Failed to save report");
    } finally {
      setSavingReport(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="vessel-search-page">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-serif text-[#0b3e65] flex items-center gap-2">
              <Ship className="w-5 h-5" />
              Vessel Search & Screening
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <Tabs value={searchType} onValueChange={setSearchType}>
              <TabsList className="mb-4">
                <TabsTrigger value="imo" data-testid="tab-imo">Search by IMO</TabsTrigger>
                <TabsTrigger value="name" data-testid="tab-name">Search by Name</TabsTrigger>
              </TabsList>
              <TabsContent value="imo">
                <form onSubmit={handleSearch} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="imo">IMO Number</Label>
                    <div className="flex gap-2">
                      <Input
                        id="imo"
                        placeholder="e.g., 9304239"
                        value={imoNumber}
                        onChange={(e) => setImoNumber(e.target.value)}
                        className="max-w-xs rounded-sm"
                        data-testid="input-imo"
                      />
                      <Button
                        type="submit"
                        disabled={loading || !imoNumber}
                        className="bg-[#0b3e65] hover:bg-[#0b3e65]/90 rounded-sm"
                        data-testid="btn-search-imo"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                        <span className="ml-2">Search</span>
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">
                      Searches Equasis database and screens against OFAC, UN, EU, and OFSI sanctions lists
                    </p>
                  </div>
                </form>
              </TabsContent>
              <TabsContent value="name">
                <form onSubmit={(e) => { e.preventDefault(); handleSearch(e); }} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="vesselName">Vessel Name</Label>
                    <div className="flex gap-2">
                      <Input
                        id="vesselName"
                        placeholder="e.g., AC RENHE"
                        value={vesselName}
                        onChange={(e) => setVesselName(e.target.value)}
                        className="max-w-xs rounded-sm"
                        data-testid="input-vessel-name"
                      />
                      <Button
                        type="submit"
                        disabled={loading || !vesselName}
                        className="bg-[#f37022] hover:bg-[#f37022]/90 rounded-sm"
                        data-testid="btn-screen-name"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                        <span className="ml-2">Search</span>
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">
                      Screens vessel name against OFAC SDN, UN Security Council, EU Consolidated, and UK OFSI
                    </p>
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="w-10 h-10 animate-spin text-[#0b3e65]" />
            <p className="text-sm text-slate-500">Screening against sanctions lists...</p>
            <p className="text-xs text-slate-400">This may take 30-60 seconds</p>
          </div>
        )}

        {result && (
          <div className="space-y-6">
            <RiskIndicator
              risk={result.overall_risk}
              title={`Overall Risk: ${result.overall_risk === "GREEN" ? "Low Risk - Compliant" : result.overall_risk === "YELLOW" ? "Medium Risk - Investigation Required" : "High Risk - Do Not Proceed"}`}
              description={result.vessel_screening?.notes || "Screening complete."}
            />

            {/* Source Status */}
            {result.source_statuses?.length > 0 && (
              <Card>
                <CardHeader className="pb-2 border-b border-slate-100">
                  <CardTitle className="text-sm font-semibold text-slate-600">Sanctions Sources Status</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {result.source_statuses.map((s, i) => (
                      <div key={i} className={`p-3 rounded-sm border text-sm ${
                        s.status === "checked" ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
                      }`}>
                        <p className="font-medium text-xs truncate">{s.name}</p>
                        <p className={`text-xs mt-1 ${s.status === "checked" ? "text-emerald-600" : "text-red-600"}`}>
                          {s.status === "checked" ? `Checked (${s.entities_checked} entities)` : `Failed: ${s.error || "Unknown"}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Vessel Data */}
            {result.vessel_data && (
              <Card>
                <CardHeader className="border-b border-slate-100">
                  <CardTitle className="text-lg font-serif text-[#0b3e65]">Vessel Information</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {[
                      { label: "Vessel Name", value: result.vessel_data.vessel_name },
                      { label: "IMO Number", value: result.vessel_data.imo_number, mono: true },
                      { label: "Flag", value: result.vessel_data.flag },
                      { label: "Vessel Type", value: result.vessel_data.vessel_type },
                      { label: "Gross Tonnage", value: result.vessel_data.gross_tonnage },
                      { label: "DWT", value: result.vessel_data.dwt },
                      { label: "Build Year", value: result.vessel_data.build_year },
                      { label: "MMSI", value: result.vessel_data.mmsi, mono: true },
                    ].map((item, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-sm">
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{item.label}</p>
                        <p className={item.mono ? "font-mono" : "font-medium"}>{item.value || "N/A"}</p>
                      </div>
                    ))}
                  </div>

                  {/* Classification & P&I */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="p-4 border border-slate-200 rounded-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="w-4 h-4 text-[#0b3e65]" />
                        <p className="text-xs text-slate-500 uppercase">Classification Society</p>
                      </div>
                      <p className="font-medium">{result.vessel_data.classification_society || "N/A"}</p>
                      {result.vessel_data.classification_society && (
                        <p className={`text-xs mt-1 ${result.vessel_data.class_is_iacs_member ? "text-emerald-600" : "text-amber-600"}`}>
                          {result.vessel_data.class_is_iacs_member ? "IACS Member" : "Not IACS Member"}
                        </p>
                      )}
                    </div>
                    <div className="p-4 border border-slate-200 rounded-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-[#0b3e65]" />
                        <p className="text-xs text-slate-500 uppercase">P&I Club</p>
                      </div>
                      <p className="font-medium">{result.vessel_data.pandi_club || "N/A"}</p>
                      {result.vessel_data.pandi_club && (
                        <p className={`text-xs mt-1 ${result.vessel_data.pandi_is_igp_member ? "text-emerald-600" : "text-amber-600"}`}>
                          {result.vessel_data.pandi_is_igp_member ? "IGP&I Member" : "Not IGP&I Member"}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Flag Risk */}
                  {result.vessel_data.flag_risk && (
                    <div className={`p-4 rounded-sm border mb-6 ${
                      result.vessel_data.flag_risk.flag_list === "BLACK" ? "bg-red-50 border-red-200" :
                      result.vessel_data.flag_risk.flag_list === "GREY" ? "bg-amber-50 border-amber-200" :
                      "bg-emerald-50 border-emerald-200"
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Flag className="w-5 h-5" />
                          <div>
                            <p className="font-medium">Flag: {result.vessel_data.flag_risk.flag_country}</p>
                            <p className="text-sm text-slate-600">Paris MoU Classification</p>
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          result.vessel_data.flag_risk.flag_list === "BLACK" ? "bg-red-100 text-red-700" :
                          result.vessel_data.flag_risk.flag_list === "GREY" ? "bg-amber-100 text-amber-700" :
                          "bg-emerald-100 text-emerald-700"
                        }`}>
                          {result.vessel_data.flag_risk.flag_list} LIST
                        </span>
                      </div>
                      {result.vessel_data.flag_risk.flag_of_convenience && (
                        <p className="text-sm text-amber-600 mt-2">Flag of Convenience (ITF)</p>
                      )}
                    </div>
                  )}

                  {/* Ownership Entities */}
                  {result.vessel_data.ownership_entities?.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-[#0b3e65] mb-4 flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        Ownership Structure
                      </h4>
                      <div className="space-y-3">
                        {result.vessel_data.ownership_entities.map((entity, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-100">
                            <div>
                              <p className="text-xs text-slate-500 uppercase">{entity.role}</p>
                              <p className="font-medium text-sm">{entity.name}</p>
                              {entity.country && <p className="text-xs text-slate-400">{entity.country}</p>}
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${
                              entity.sanction_status === "Not Sanctioned" ? "bg-emerald-100 text-emerald-700" :
                              entity.sanction_status === "Not checked" ? "bg-slate-100 text-slate-600" :
                              "bg-red-100 text-red-700"
                            }`}>
                              {entity.sanction_status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Vessel Screening */}
            {result.vessel_screening && (
              <Card>
                <CardHeader className="border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-serif text-[#0b3e65]">Sanctions Screening</CardTitle>
                    <RiskBadge risk={result.vessel_screening.risk_level} />
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="mb-4">
                    <span className="text-slate-500 text-sm">Screened Against:</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {result.vessel_screening.screened_sources?.map((source, idx) => (
                        <span key={idx} className="px-2 py-1 bg-[#0b3e65]/10 text-[#0b3e65] text-xs rounded-sm font-medium">
                          {source}
                        </span>
                      ))}
                    </div>
                  </div>
                  {result.vessel_screening.sanctions_matches?.length > 0 ? (
                    <div className="bg-red-50 border border-red-200 rounded-sm p-4">
                      <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Potential Matches ({result.vessel_screening.sanctions_matches.length})
                      </h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Source</TableHead>
                            <TableHead>Matched Entity</TableHead>
                            <TableHead>Score</TableHead>
                            <TableHead>Program</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {result.vessel_screening.sanctions_matches.map((m, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{m.source}</TableCell>
                              <TableCell>{m.entity_name}</TableCell>
                              <TableCell className="font-mono font-semibold text-red-600">{m.match_score.toFixed(1)}%</TableCell>
                              <TableCell>{m.program || "N/A"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-emerald-50 rounded-sm border border-emerald-200">
                      <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
                      <p className="text-emerald-700 font-medium">No sanctions matches found</p>
                      <p className="text-sm text-emerald-600">Entity cleared against all screened lists</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Risk Factors */}
            {result.risk_factors?.length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                    <AlertOctagon className="w-4 h-4" />
                    Risk Factors ({result.risk_factors.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {result.risk_factors.map((rf, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                          rf.severity === "HIGH" ? "bg-red-100 text-red-700" :
                          rf.severity === "MEDIUM" ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-700"
                        }`}>{rf.severity}</span>
                        <span className="text-amber-900">{rf.description}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Save Button */}
            <div className="flex justify-end gap-3">
              <Button
                onClick={handleSaveReport}
                disabled={savingReport || !result.vessel_screening}
                className="bg-[#0b3e65] hover:bg-[#0b3e65]/90 rounded-sm"
                data-testid="btn-save-report"
              >
                {savingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                Save Compliance Report
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VesselSearchPage;
